import { useContext } from "react";
import Skeleton from "@mui/material/Skeleton";

import { StateContext } from "../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../context/globalContext/interface";
import { APP_CONFIG } from "../../../../constant/common";
import styles from "./bookingFormSkeleton.module.scss";
import { br } from "../../../../styles/design-tokens";

export type BookingFormSkeletonType = "stays" | "flights" | "flights-stays" | "activities";

type BookingFormSkeletonProps = {
  readonly type: BookingFormSkeletonType;
  readonly withHeader?: boolean;
};

// Shared MUI-Skeleton factory — matches the project convention
// (ItineraryDetailsSkeleton / SharedCartReviewSkeleton): wave + rectangular,
// per-element borderRadius via inline sx. No custom shimmer palette.
const sk = (props: any) => <Skeleton animation="wave" variant="rectangular" {...props} />;

function HeaderSkeleton() {
  return (
    <div className={styles.header}>
      {sk({ width: 40, height: 40, sx: { borderRadius: "50%" } })}
      {sk({ width: 140, height: 20, sx: { borderRadius: br(4) } })}
    </div>
  );
}

// TabSelector placeholder — shown above EVERY form type (the real TabSelector
// sits above both the flights/stays card and the activities form). Mirrors
// TabSelector's options gate: 4 pills when appConfig `ENABLE_ACTIVITIES` is on
// (stays | flights | activities | flights + stays), else 3 (stays | flights |
// flights + stays).
function TabBarSkeleton() {
  const state = useContext(StateContext);
  const { appConfig }: iGlobalContext = state;
  const isActivitiesEnabled =
    appConfig?.find((item: any) => item.key === APP_CONFIG.ENABLE_ACTIVITIES)?.value === "true";
  const pillWidths = isActivitiesEnabled ? [72, 84, 96, 132] : [72, 84, 132];
  return (
    <div className={styles.tabBar}>
      {pillWidths.map((width, index) => (
        <span key={index}>{sk({ width, height: 36, sx: { borderRadius: br(18) } })}</span>
      ))}
    </div>
  );
}

// Stays / Flights / Flights+Stays share the same card chrome (TabSelector +
// `.searchNewContainer` card with a title row and a fields body). Only the
// field rows inside `.card2` differ by type — mirrors hotelInputFields.tsx
// (stays) vs inputFields.tsx (flights). Flights+Stays opens on the Flights tab,
// so it renders the flights field set.
function FlightsStaysSkeleton({ type }: { type: Exclude<BookingFormSkeletonType, "activities"> }) {
  const isStays = type === "stays";
  return (
    <div className={styles.contentCard}>
      <div className={styles.searchCard}>
        {/* card1 — title + preferences gear */}
        <div className={styles.card1}>
          {sk({ width: 90, height: 24, sx: { borderRadius: br(4) } })}
          {sk({ width: 40, height: 40, sx: { borderRadius: "50%" } })}
        </div>

        {/* card2 — type-specific field rows */}
        <div className={styles.card2}>
          {isStays ? (
            <>
              {/* travelers + rooms */}
              {sk({ width: "100%", height: 56, sx: { borderRadius: br(12) } })}
              {/* location */}
              {sk({ width: "100%", height: 56, sx: { borderRadius: br(12) } })}
              {/* check-in / check-out */}
              {sk({ width: "100%", height: 56, sx: { borderRadius: br(12) } })}
              {/* "Add stay" pill */}
              {sk({ width: 120, height: 40, sx: { borderRadius: br(200) } })}
            </>
          ) : (
            <>
              {/* trip type / cabin + travelers */}
              {sk({ width: "100%", height: 56, sx: { borderRadius: br(12) } })}
              {/* From airport */}
              {sk({ width: "100%", height: 56, sx: { borderRadius: br(12) } })}
              {/* To airport */}
              {sk({ width: "100%", height: 56, sx: { borderRadius: br(12) } })}
              {/* date range */}
              {sk({ width: "100%", height: 56, sx: { borderRadius: br(12) } })}
            </>
          )}
          {/* Search button */}
          {sk({ width: "100%", height: 46, sx: { borderRadius: br(100) } })}
        </div>
      </div>
    </div>
  );
}

// Activities renders ActivitySearchForm (a single `.container` card with a
// header row + content area), not the flights/stays card chrome. The "Add
// activity" pill is gated on the SAME flag the real form uses — appConfig
// `ENABLE_MULTI_ACTIVITY` (on for some domains, off for others) — so the
// skeleton matches the live form per-domain. The real form's extra
// `forms.length < MAX_ACTIVITY_FORMS` clause is always true here (one form on
// open), so the flag alone decides it.
function ActivitiesSkeleton() {
  const state = useContext(StateContext);
  const { appConfig }: iGlobalContext = state;
  const isMultiActivityEnabled =
    appConfig?.find((item: any) => item.key === APP_CONFIG.ENABLE_MULTI_ACTIVITY)?.value === "true";

  return (
    <div className={styles.activityContainer}>
      <div className={styles.headerRow}>
        {sk({ width: 110, height: 24, sx: { borderRadius: br(4) } })}
        {sk({ width: 40, height: 40, sx: { borderRadius: "50%" } })}
      </div>
      <div className={styles.contentArea}>
        {/* destination input */}
        {sk({ width: "100%", height: 48, sx: { borderRadius: br(12) } })}
        {/* date range */}
        {sk({ width: "100%", height: 48, sx: { borderRadius: br(12) } })}
        {/* "Add activity" pill — flag-gated, mirrors ActivitySearchForm */}
        {isMultiActivityEnabled && (
          <div className={styles.activityAddMoreCard}>
            {sk({ width: 140, height: 40, sx: { borderRadius: br(200) } })}
          </div>
        )}
        {/* Search button */}
        {sk({ width: "100%", height: 46, sx: { borderRadius: br(100) } })}
      </div>
    </div>
  );
}

/**
 * Form-shaped placeholder shown in place of the generic 3-dot <Loader/> while
 * the Shop-Travel booking SEARCH form is loading on slow device/network. Mirrors
 * the active form's layout per `type` so there's no visual jump when the real
 * form paints. Rendered from ContentContainer's streaming branch, scoped to the
 * search-form step only (listing/review keep the 3-dot loader).
 *
 * The TabSelector placeholder sits above EVERY type (incl. activities), matching
 * the real layout where the tab bar renders above both the flights/stays card
 * and the activities form.
 */
export default function BookingFormSkeleton({ type, withHeader = false }: BookingFormSkeletonProps): JSX.Element {
  return (
    <>
      {withHeader && <HeaderSkeleton />}
      <TabBarSkeleton />
      {type === "activities" ? <ActivitiesSkeleton /> : <FlightsStaysSkeleton type={type} />}
    </>
  );
}
